import { JOB_STATUS } from '@/types/jobs';
import {
  JobStepsBreakdown,
  JobTechnicalDetails,
  RelatedCommonResult,
  RelatedCommonType,
  RelatedContextForHit,
  RelatedRecentJob,
  RelatedWorks,
  SearchOutcomeHit,
} from '@/types/search';
import { getJobStatusLabel } from '@/utils/job';

const RESULT_NOISE = new Set([
  'please select job result here',
  'please select',
]);

export const safeParseJson = <T>(value: unknown): T | null => {
  if (value == null) return null;

  if (typeof value === 'object') {
    return value as T;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
};

export const toStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
};

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

export const parseJobTechnicalDetails = (
  raw: unknown
): JobTechnicalDetails | null => {
  const parsed = safeParseJson<Record<string, unknown>>(raw);
  if (!parsed || Array.isArray(parsed)) return null;

  const diagnostic =
    toOptionalString(parsed.diagnostic_phase) ??
    toOptionalString(parsed.diagnostic);
  const action =
    toOptionalString(parsed.action_phase) ?? toOptionalString(parsed.action);
  const verification =
    toOptionalString(parsed.verification_phase) ??
    toOptionalString(parsed.verification);
  const equipment = toStringList(
    parsed.equipment_used ?? parsed.equipment ?? parsed.equipmentUsed
  );

  if (!diagnostic && !action && !verification && equipment.length === 0) {
    return null;
  }

  return {
    diagnostic,
    action,
    verification,
    equipment,
  };
};

export const parseJobStepsBreakdown = (
  raw: unknown
): JobStepsBreakdown | null => {
  const parsed = safeParseJson<Record<string, unknown>>(raw);
  if (!parsed || Array.isArray(parsed)) return null;

  const coreCandidate = parsed.technical_core;
  const core =
    coreCandidate &&
    typeof coreCandidate === 'object' &&
    !Array.isArray(coreCandidate)
      ? (coreCandidate as Record<string, unknown>)
      : parsed;

  const steps: JobStepsBreakdown = {
    actions: toStringList(core.actions),
    conditions: toStringList(core.conditions),
    followUp: toStringList(core.follow_up ?? core.followUp),
    equipment: toStringList(core.equipment),
    measurements: toStringList(core.measurements),
  };

  const hasContent =
    steps.actions.length > 0 ||
    steps.conditions.length > 0 ||
    steps.followUp.length > 0 ||
    steps.equipment.length > 0 ||
    steps.measurements.length > 0;

  return hasContent ? steps : null;
};

export const hasExpandableOutcomeContent = (input: {
  technicalDetails: JobTechnicalDetails | null;
  stepsBreakdown: JobStepsBreakdown | null;
}): boolean => {
  return Boolean(input.technicalDetails || input.stepsBreakdown);
};

type JobVh3AiBlock = {
  job_natural_summary?: string | null;
  job_description?: string | null;
  job_technical_details?: unknown;
  job_steps_breakdown?: unknown;
};

export type JobSeeMoreContent = {
  summary: string | null;
  fullText: string | null;
  technicalDetails: JobTechnicalDetails | null;
  stepsBreakdown: JobStepsBreakdown | null;
  hasDetails: boolean;
};

/** Parse getJob `vh3_ai` into the same see-more body Similar jobs use. */
export const getJobSeeMoreFromVh3Ai = (
  vh3Ai?: unknown
): JobSeeMoreContent => {
  const block =
    Array.isArray(vh3Ai) && vh3Ai[0] && typeof vh3Ai[0] === 'object'
      ? (vh3Ai[0] as JobVh3AiBlock)
      : null;

  const summary = block?.job_natural_summary?.trim() || null;
  const description = block?.job_description?.trim() || null;
  const fullText =
    description && description !== summary ? description : null;
  const technicalDetails = parseJobTechnicalDetails(
    block?.job_technical_details
  );
  const stepsBreakdown = parseJobStepsBreakdown(block?.job_steps_breakdown);

  return {
    summary,
    fullText,
    technicalDetails,
    stepsBreakdown,
    hasDetails: hasExpandableOutcomeContent({
      technicalDetails,
      stepsBreakdown,
    }),
  };
};

export const getRelatedContextForHit = (
  relatedWorks: RelatedWorks | undefined,
  hit: SearchOutcomeHit
): RelatedContextForHit => {
  const typeKey =
    hit.typeId != null && !Number.isNaN(Number(hit.typeId))
      ? String(hit.typeId)
      : null;
  const engineerKey =
    hit.resourceId != null && !Number.isNaN(Number(hit.resourceId))
      ? String(hit.resourceId)
      : null;
  const siteKey = hit.siteKey?.trim() ? hit.siteKey.trim() : null;

  return {
    type:
      typeKey && relatedWorks?.byType
        ? (relatedWorks.byType[typeKey] ?? null)
        : null,
    engineer:
      engineerKey && relatedWorks?.byEngineer
        ? (relatedWorks.byEngineer[engineerKey] ?? null)
        : null,
    site:
      siteKey && relatedWorks?.bySite
        ? (relatedWorks.bySite[siteKey] ?? null)
        : null,
  };
};

export const normalizeResultLabel = (result: string): string => {
  const trimmed = result.trim();
  const lower = trimmed.toLowerCase();

  if (lower === 'complete' || lower === 'completed') return 'Completed';
  if (
    lower === 'further work required' ||
    lower === 'further works required'
  ) {
    return 'Further Work Required';
  }
  if (lower === 'additional works required') return 'Additional Works Required';
  if (lower === 'no access') return 'No Access';
  if (lower === 'ongoing job') return 'Ongoing Job';
  if (lower === 'completed with issues') return 'Completed with issues';

  return trimmed;
};

export const isNoiseResultLabel = (result: string): boolean => {
  return RESULT_NOISE.has(result.trim().toLowerCase());
};

export const aggregateCommonResults = (
  items: RelatedCommonResult[] | undefined,
  limit = 5
): Array<{ label: string; count: number }> => {
  if (!items?.length) return [];

  const totals = new Map<string, number>();

  for (const item of items) {
    if (!item?.result || typeof item.count !== 'number') continue;
    if (isNoiseResultLabel(item.result)) continue;

    const label = normalizeResultLabel(item.result);
    totals.set(label, (totals.get(label) ?? 0) + item.count);
  }

  return Array.from(totals.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export const aggregateCommonTypes = (
  items: RelatedCommonType[] | undefined,
  limit = 5
): Array<{ label: string; count: number }> => {
  if (!items?.length) return [];

  const totals = new Map<string, number>();

  for (const item of items) {
    if (!item || typeof item !== 'object') continue;

    const raw = item as RelatedCommonType & { name?: unknown; label?: unknown };
    const labelCandidate = raw.type ?? raw.name ?? raw.label;
    if (typeof labelCandidate !== 'string') continue;

    const label = labelCandidate.trim();
    if (!label) continue;

    const count =
      typeof item.count === 'number'
        ? item.count
        : typeof item.count === 'string'
          ? Number(item.count)
          : NaN;
    if (!Number.isFinite(count)) continue;

    totals.set(label, (totals.get(label) ?? 0) + count);
  }

  return Array.from(totals.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export const formatRelatedJobStatus = (
  status: string | undefined | null
): string | null => {
  if (!status || typeof status !== 'string') return null;
  const trimmed = status.trim();
  if (!trimmed) return null;

  if (Object.values(JOB_STATUS).includes(trimmed as JOB_STATUS)) {
    return getJobStatusLabel(trimmed as JOB_STATUS);
  }

  return trimmed;
};

export const formatRelatedJobResult = (
  result: string | undefined | null
): string | null => {
  if (!result || typeof result !== 'string') return null;
  if (isNoiseResultLabel(result)) return null;
  const label = normalizeResultLabel(result);
  return label || null;
};

export const resolveRelatedJobId = (
  job: RelatedRecentJob,
  hits?: SearchOutcomeHit[]
): number | null => {
  if (typeof job.jobId === 'number' && Number.isFinite(job.jobId)) {
    return job.jobId;
  }

  const ref = job.ref?.trim();
  if (!ref || !hits?.length) return null;

  const match = hits.find(
    hit => typeof hit.jobRef === 'string' && hit.jobRef.trim() === ref
  );
  return match?.jobId ?? null;
};

export const formatAvgEndDelta = (mins: number | undefined | null): string => {
  if (typeof mins !== 'number' || Number.isNaN(mins)) return '—';

  const abs = Math.abs(mins);
  const direction = mins === 0 ? 'on time' : mins > 0 ? 'late' : 'early';

  if (mins === 0) return 'On time';

  if (abs >= 60 * 24 * 7) {
    const weeks = Math.min(Math.round(abs / (60 * 24 * 7)), 99);
    return `~${weeks}w ${direction}`;
  }

  if (abs >= 60 * 24) {
    const days = Math.round(abs / (60 * 24));
    return `~${days}d ${direction}`;
  }

  if (abs >= 60) {
    const hours = Math.round(abs / 60);
    return `~${hours}h ${direction}`;
  }

  return `~${Math.round(abs)}m ${direction}`;
};

export const formatCompletionRate = (
  rate: number | undefined | null
): string => {
  if (typeof rate !== 'number' || Number.isNaN(rate)) return '—';
  return `${Math.round(rate * 10) / 10}%`;
};

export const formatJobCount = (count: number | undefined | null): string => {
  if (typeof count !== 'number' || Number.isNaN(count)) return '—';
  return count.toLocaleString();
};
